import React, { useEffect, useRef, useState, useContext } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { HDRequestStatus, ROI } from "helpers/interfaces";
import { GalleryItem } from "helpers/types";
import {
    calculateAbsoluteROIValues,
    calculateLineWidth,
    CustomLink,
    getImageFilename,
    getLocaleDateString,
    getTimeIn12h,
    getWeekDay,
    roiColor,
    roundOff,
    getMediaFileDisplayNameDependingOnContext
} from "helpers/utils";
import classNames from "classnames";
import cc_dark_bg_logo from "assets/images/covert-captures-logo-dark-bg.png"
import { CaseImagesContext } from "pages/cases/view/case-images/CaseImages";
import VideoCreationApi from "api/video-creation-api";

// Preloaded once at module load so the crop-export painter can draw it synchronously.
// Same asset as the DOM overlay's logo - screen and export must stay pixel-identical.
const exportLogoImage = new Image();
exportLogoImage.src = cc_dark_bg_logo;

interface IProps {
    show: boolean;
    isSaving: boolean;
    onHide: () => void;
    showROI?: boolean;
    onSaveRegionsOfInterest: (regions: (ROIRectangle | ROI)[]) => void;
    imageSrc: string;
    imageInfo: GalleryItem;
    regions: ROI[];
    onNext: () => void;
    onPrev: () => void;
    onHdRequested: (index: number, imageId: number) => void;
    singleImageCheckboxHandler: (imageId: number) => void;
    // Opens the given image in a separate browser tab (wired up from CaseImages)
    onGoToImageInNewTab: (sequence: string | number) => void;
    toggleMediaLockStatus: (imageId: number) => void;
    isImageMarkedAsLocked: (imageId: number) => boolean;
    mode?: UserMode;
    isSelect: boolean;
    imageIndex: number;
    galleryName: string;
    // Create Video tab only: runs the same assign action as the toolbar button
    onAssignVideoImages?: () => void;
}

type ROIRectangle = {
    relative_x: number;
    relative_y: number;
    relative_width: number;
    relative_height: number;
}

enum UserMode {
    VIEW = "view",
    HOVER = "hover",
    DRAW = "draw",
    MOVE = "move",
    RESIZE = "resize",
    ZOOM_PAN = "pan_zoom",
    CROP = "crop"
}

enum PointerType {
    NWSE_RESIZE = "nwse-resize",
    NESW_RESIZE = "nesw-resize",
    MOVE = "move",
    CROSSHAIR = "crosshair",
    EW_RESIZE = "ew-resize",
    NS_RESIZE = "ns-resize"
}

const getCursorForPointerPosition = (position: any) => {
    switch (position) {
        case "tl":
        case "br":
            return PointerType.NWSE_RESIZE;
        case "tr":
        case "bl":
            return PointerType.NESW_RESIZE;
        case "left":
        case "right":
            return PointerType.EW_RESIZE
        case "top":
        case "bottom":
            return PointerType.NS_RESIZE
        case "inside":
            return PointerType.MOVE;
        default:
            return "crosshair";
    }
};

const getModeForCursorPosition = (position: any): UserMode => {
    switch (position) {
        case "inside":
            return UserMode.MOVE;
        case "tl":
        case "br":
        case "tr":
        case "bl":
        case "top":
        case "bottom":
        case "left":
        case "right":
            return UserMode.RESIZE
        default:
            return UserMode.DRAW;
    }
}

const getResizedCoordinates = (clientX: number, clientY: number, pointerPosition: any, selectedROI: ROIRectangle) => {
    switch (pointerPosition) {
        case "top":
            return {
                relative_x: selectedROI.relative_x,
                relative_y: clientY,
                relative_width: selectedROI.relative_width,
                relative_height: roundOff((selectedROI.relative_height + (selectedROI.relative_y - clientY)))
            };
        case "left":
            return {
                relative_x: clientX,
                relative_y: selectedROI.relative_y,
                relative_width: roundOff((selectedROI.relative_width + (selectedROI.relative_x - clientX))),
                relative_height: selectedROI.relative_height
            };
        case "right":
            return {
                relative_x: selectedROI.relative_x,
                relative_y: selectedROI.relative_y,
                relative_width: roundOff(clientX - selectedROI.relative_x),
                relative_height: selectedROI.relative_height
            };
        case "bottom":
            return {
                relative_x: selectedROI.relative_x,
                relative_y: selectedROI.relative_y,
                relative_width: selectedROI.relative_width,
                relative_height: roundOff(clientY - selectedROI.relative_y)
            };
        case "tl":
            return {
                relative_x: clientX,
                relative_y: clientY,
                relative_width: roundOff((selectedROI.relative_width + (selectedROI.relative_x - clientX))),
                relative_height: roundOff((selectedROI.relative_height + (selectedROI.relative_y - clientY)))
            };
        case "tr":
            return {
                relative_x: selectedROI.relative_x,
                relative_y: clientY,
                relative_width: roundOff(clientX - selectedROI.relative_x),
                relative_height: roundOff((selectedROI.relative_height + (selectedROI.relative_y - clientY)))
            };
        case "bl":
            return {
                relative_x: clientX,
                relative_y: selectedROI.relative_y,
                relative_width: roundOff((selectedROI.relative_width + (selectedROI.relative_x - clientX))),
                relative_height: roundOff(clientY - selectedROI.relative_y)
            };
        case "br":
            return {
                relative_x: selectedROI.relative_x,
                relative_y: selectedROI.relative_y,
                relative_width: roundOff(clientX - selectedROI.relative_x),
                relative_height: roundOff(clientY - selectedROI.relative_y)
            };
        // Leave as is
        default:
            return {...selectedROI}
    }
}

const videoCreationApi = new VideoCreationApi()

// Crop box stroke — distinct from roiColor (teal ROIs) and red (ROI highlight)
const cropBoxColor = '#ffc107';

// Maps a box drawn on the displayed (cropped) image into original-image fractions, so
// repeated crops always narrow relative to the original. Deliberately NOT roundOff'd:
// repeated narrowing can produce values a 3-decimal round would collapse to zero.
const composeCropBoxes = (active: ROIRectangle, inner: ROIRectangle): ROIRectangle => ({
    relative_x: active.relative_x + inner.relative_x * active.relative_width,
    relative_y: active.relative_y + inner.relative_y * active.relative_height,
    relative_width: inner.relative_width * active.relative_width,
    relative_height: inner.relative_height * active.relative_height
});

// Fixed video crop aspect ratio (width:height). The backend's video content-area
// enforcement uses the same ratio — keep them in sync.
const VIDEO_CROP_ASPECT_W = 16;
const VIDEO_CROP_ASPECT_H = 9;

// Constrain a crop drag's end point so the box keeps the fixed 16:9 video aspect ratio
// in image pixels. Coordinates are screen fractions (0..1 of the canvas box);
// canvasWidth/Height are the displayed bitmap's pixel dims, converting fraction space to
// pixel space. Uniform zoom scales both axes equally, so the pixel ratio survives any
// zoom/pan; on a re-crop of an already-locked (16:9) cropped bitmap the formula
// self-adapts through the current canvas dims and stays 16:9 in original pixels.
// Growth is capped by whichever image edge the drag reaches first, preserving the ratio.
const constrainCropEndToVideoAR = (startX: number, startY: number, endX: number, endY: number,
                                   canvasWidth: number, canvasHeight: number) => {
    // Screen-fraction dy per dx that yields a VIDEO_CROP_ASPECT_W:H box in pixels
    const dyPerDx = (canvasWidth * VIDEO_CROP_ASPECT_H) / (canvasHeight * VIDEO_CROP_ASPECT_W);
    const signX = endX < startX ? -1 : 1;
    const signY = endY < startY ? -1 : 1;
    const roomX = signX < 0 ? startX : 1 - startX;
    const roomY = signY < 0 ? startY : 1 - startY;
    // The dominant drag axis drives the size; caps keep both axes inside the image
    const deltaX = Math.min(
        Math.max(Math.abs(endX - startX), Math.abs(endY - startY) / dyPerDx),
        roomX,
        roomY / dyPerDx
    );
    return {endX: startX + signX * deltaX, endY: startY + signY * deltaX * dyPerDx};
};

// The seeded/reset "no crop" video ROI (see CreateVideo's image-set creation)
const FULL_FRAME_VIDEO_ROI: ROIRectangle = {relative_x: 0, relative_y: 0, relative_width: 1, relative_height: 1};

const isValidRelativeBox = (roi: any): roi is ROIRectangle =>
    !!roi && [roi.relative_x, roi.relative_y, roi.relative_width, roi.relative_height]
        .every((v: any) => typeof v === 'number');

const isFullFrameBox = (roi: ROIRectangle) =>
    roi.relative_x === 0 && roi.relative_y === 0 && roi.relative_width === 1 && roi.relative_height === 1;

// Draws the date bar into an EXPORT canvas at the literal .popup-date-overlay CSS metrics
// (see _gallery.scss) scaled by devicePixelRatio — screen-true, not image-proportional. On
// screen the bar is a live DOM overlay; exports emulate a PrintScreen of the preview, so the
// two must agree pixel-for-pixel at the preview's displayed size.
const drawDateBarAtScreenMetrics = (ctx: CanvasRenderingContext2D, width: number, height: number, dpr: number, capturedAt: string, showLogo: boolean = false) => {
    // A missing/garbage timestamp would render the literal string "undefined" (getWeekDay
    // returns days[NaN]) — skip the bar, keep the export
    if (!capturedAt || isNaN(new Date(capturedAt).getTime())) {
        return;
    }
    // Same helpers as the DOM overlay — content parity is the contract
    const segments = [
        getLocaleDateString(capturedAt) + ',',
        getTimeIn12h(capturedAt) + ',',
        getWeekDay(capturedAt)
    ];

    // The literal .popup-date-overlay values: 40px bar, 1.2rem (19.2px) font, 2px
    // letter-spacing, 1rem (16px) gaps — at device-pixel scale
    const barHeight = 40 * dpr;
    let font = 19.2 * dpr;
    let letterSpacing = 2 * dpr;
    let gap = 16 * dpr;

    const setFont = (px: number) => {
        // ctx.font silently IGNORES invalid strings (keeping the previous value), so assign a
        // guaranteed-parsing stack first and the exact app body stack second — if a parser
        // rejects the second, we keep a good fallback instead of the default 10px sans-serif
        ctx.font = `${px}px 'Segoe UI', Roboto, Arial, sans-serif`;
        ctx.font = `${px}px -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif`;
        if ('letterSpacing' in ctx) {
            // TS 4.9's dom lib predates letterSpacing (added ~TS 5.1) — the cast is required.
            // measureText reflects it, and measure/draw share this context, so no manual math.
            (ctx as any).letterSpacing = `${letterSpacing}px`;
        }
    };

    const measureLine = () => {
        const widths = segments.map(segment => ctx.measureText(segment).width);
        const total = widths.reduce((sum, w) => sum + w, 0) + 2 * gap;
        return {widths, total};
    };

    setFont(font);
    let {widths, total} = measureLine();
    if (total > width * 0.95) {
        // Degenerate window widths only. On screen the DOM bar just overflows; in a saved
        // artifact clipped text is forever — shrink the text proportionally in one pass
        // (metrics are font-linear). The single deliberate WYSIWYG deviation; the bar
        // geometry itself stays at screen height like the DOM bar does.
        const scale = (width * 0.95) / total;
        font = font * scale;
        letterSpacing = letterSpacing * scale;
        gap = gap * scale;
        setFont(font);
        ({widths, total} = measureLine());
    }

    // Single-use export context — no save/restore needed (unlike the shared on-screen
    // canvas context, where state hygiene is mandatory)
    ctx.fillStyle = 'black';
    ctx.fillRect(0, height - barHeight, width, barHeight);
    ctx.fillStyle = 'white';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    // Manual three-segment layout mirrors the DOM's spans-with-flex-gap
    let x = (width - total) / 2;
    const y = height - barHeight / 2;
    segments.forEach((segment, idx) => {
        ctx.fillText(segment, x, y);
        x += widths[idx] + gap;
    });

    // The literal .popup-image-overlay-text-logo metrics (_cam-status.scss): pinned
    // right:0 bottom:0 inside the bar, img max-height 40px - at device-pixel scale.
    // Skipped silently if the asset hasn't decoded yet (module preload makes that rare).
    if (showLogo && exportLogoImage.complete && exportLogoImage.naturalWidth > 0) {
        const cssLogoHeight = Math.min(40, exportLogoImage.naturalHeight);
        const logoHeight = cssLogoHeight * dpr;
        const logoWidth = exportLogoImage.naturalWidth * (cssLogoHeight / exportLogoImage.naturalHeight) * dpr;
        ctx.drawImage(exportLogoImage, width - logoWidth, height - logoHeight, logoWidth, logoHeight);
    }
};

const ImagePopupModal = (props: IProps) => {
    const [roiReferenceImage, setRoiReferenceImage] = useState<HTMLImageElement>();
    const canvasRef = useRef(null);
    const [roiRectangles, setRoiRectangles] = useState<(ROI | ROIRectangle)[]>([...props.regions]);
    const [mode, setMode] = useState(UserMode.VIEW);
    const [startX, setStartX] = useState(0);
    const [startY, setStartY] = useState(0);
    const [dragOffsetX, setDragOffsetX] = useState(0);
    const [dragOffsetY, setDragOffsetY] = useState(0);
    const [highlightedROIRectangle, setHighlightedROIRectangle] = useState<ROIRectangle | null>(null);
    const [resizingDirection, setResizingDirection] = useState<string | null>(null);
    const [showROI, setShowROI] = useState(true);
    const [showDateOverlay, setShowDateOverlay] = useState(true);
    // Deliberately off by default — the logo in the zoomed preview's date overlay is opt-in per popup.
    const [showLogoInDateOverlay, setShowLogoInDateOverlay] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [panX, setPanX] = useState(0);
    const [panY, setPanY] = useState(0);
    const [prevPanX, setPrevPanX] = useState(0);
    const [prevPanY, setPrevPanY] = useState(0);
    const [isPanning, setIsPanning] = useState(false);
    // Crop box committed by "Save Crop" — image-relative fractions of the full image,
    // valid regardless of the zoom/pan it was drawn under. Step 2 (actual cropping) consumes this.
    const [cropBoxDimensions, setCropBoxDimensions] = useState<ROIRectangle | null>(null);
    // Box drawn but not yet saved — shown for inspection until Save Crop commits it or Cancel drops it
    const [pendingCropBox, setPendingCropBox] = useState<ROIRectangle | null>(null);
    // Crop mode is entered by button/key (not pointerdown like DRAW), so move/up events can fire
    // with no drag in progress — this gate keeps them from painting or storing anything
    const [isDrawingCrop, setIsDrawingCrop] = useState(false);
    // The applied crop as a data URL — RAM only: never persisted, never written to the shared
    // images array. Persists across navigation (re-generated per image from cropBoxDimensions);
    // dropped on Reset Crop and gone on close. Stays CLEAN: the date bar is a DOM overlay on
    // screen and is drawn only into throwaway export canvases (copy/download), never into this.
    const [croppedImageSrc, setCroppedImageSrc] = useState<string | null>(null);
    // Decoded copy of croppedImageSrc for canvas drawing (mirrors roiReferenceImage)
    const [croppedReferenceImage, setCroppedReferenceImage] = useState<HTMLImageElement | null>(null);
    // Dev instrumentation for on-the-fly crop speed during navigation — grep [crop-nav] to remove
    const cropNavTimingRef = useRef<number | null>(null);
    // Shows the live DOM date bar over cropped views AND (PrintScreen semantics) decides whether
    // exports include it. Display/export-time only — the canvas repaint and the RAM crop never
    // see this flag. Survives navigation and re-crops; resets when the popup closes (unmount).
    const [showCropDateBar, setShowCropDateBar] = useState(true);
    // The Create Video tab's cropped view previews the VIDEO framing, and the render
    // pipeline draws its own banner there — so the popup's date bar (screen and export
    // alike) stays off on that tab, along with its two footer switches.
    const cropDateBarAllowedForGallery = props.galleryName !== 'Create Video';
    // Create Video only: the stored video ROI fetched on popup open, held until an image is
    // ready to apply it to (the seed effect consumes it exactly once, then nulls it)
    const [fetchedVideoRoi, setFetchedVideoRoi] = useState<ROIRectangle | null>(null);


    const {
        captureDate,
        locationInfo,
        caseImagesAreWeOnApageWhereSelectRibbonShouldShow,
        videoCreationImageSetImages,
        setVideoCreationImageSetImages,
        isImageInCreateVideoImageSet,
        getImageCornerRibbonMarkup,
        imageIsPrimaryCheckboxChecked,
        onShowToastMessage
    } = useContext(CaseImagesContext);

    // Add keyboard shortcut handler
    useEffect(() => {
        const handleKeyPress = (event: KeyboardEvent) => {
            // Only handle shortcuts if modal is shown
            if (!props.show) {
                return;
            }

            // Ctrl+C / Cmd+C copies the applied crop (same action as the Copy Image button).
            // Must run before the switch: the plain-'c' crop keybind would otherwise swallow it.
            if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'c') {
                // If the user is copying selected text (e.g. the filename), let the native copy win
                if (window.getSelection()?.toString()) {
                    return;
                }
                // No crop applied / mid-re-crop -> button unmounted -> native copy behavior untouched
                (document.querySelector('[data-action="crop-copy"]') as HTMLElement)?.click();
                return;
            }

            // Find the relevant button elements
            const selectButton = document.querySelector('[data-action="select"]');
            const lockButton = document.querySelector('[data-action="lock"]');
            const hdButton = document.querySelector('[data-action="hd"]');

            switch (event.key.toLowerCase()) {
                case 's':
                    //console.log('s was pressed');
                    // Simulate click on select button
                    (selectButton as HTMLElement)?.click();
                    break;
                case 'l':
                    //console.log('l was pressed');
                    // Simulate click on lock button
                    (lockButton as HTMLElement)?.click();
                    break;
                case 'h':
                    //console.log('h was pressed');
                    // Simulate click on HD button
                    (hdButton as HTMLElement)?.click();
                    break;
                case 'a':
                    //console.log('a was pressed');
                    // 'a' only ever turns things on — each action is skipped when already
                    // active, so pressing it never de-selects, unlocks or re-requests HD.
                    if (!imageIsPrimaryCheckboxChecked(props.imageInfo.id, props.galleryName)) {
                        (selectButton as HTMLElement)?.click();
                    }
                    if (!props.isImageMarkedAsLocked(props.imageInfo.id)) {
                        (lockButton as HTMLElement)?.click();
                    }
                    if (props.imageInfo.hd_request_status !== HDRequestStatus.PENDING &&
                        props.imageInfo.hd_file_path === null) {
                        (hdButton as HTMLElement)?.click();
                    }
                    break;
                case 'c':
                    // Same button is "Crop Image" (enters crop mode) or "Save Crop" (commits the
                    // drawn box); a disabled or unmounted button makes this a no-op natively
                    (document.querySelector('[data-action="crop"]') as HTMLElement)?.click();
                    break;
                case 'x':
                    // Cancel button only exists in crop mode — no-op everywhere else
                    (document.querySelector('[data-action="crop-cancel"]') as HTMLElement)?.click();
                    break;
                default:
                    break;
            }
        };

        // Add event listener
        document.addEventListener('keydown', handleKeyPress);

        // Clean up
        return () => {
            document.removeEventListener('keydown', handleKeyPress);
        };
        // The state checks used by the 'a' guards are dependencies so the handler
        // always sees the current image and its latest select/lock/HD state, not
        // the state captured when the popup first opened
    }, [props.show, props.imageInfo, imageIsPrimaryCheckboxChecked, props.isImageMarkedAsLocked]);

    const loadImage = () => {
        const image = new Image()
        image.src = props.imageSrc
        image.onload = () => {
            // Painting (plain original vs regenerated crop) is decided by the roiReferenceImage
            // effect, which reads fresh state — deciding here would use this stale closure
            // (e.g. Reset Crop pressed mid-load would be invisible to it)
            setRoiReferenceImage(image);
        }
        // Detach on supersession: rapid A->B->C navigation must not let B's late decode commit
        // over C's. With crop persistence a stale frame is a wrong, near-identical crop the
        // user could annotate or copy without noticing.
        return () => {
            image.onload = null;
        }
    }

    // mode is required: entering/exiting crop swaps which box setupCanvas displays (pending vs saved)
    // with no other dep changing. isDrawingCrop guarantees a cleanup repaint when a drag ends on the
    // sub-min-size path, where pendingCropBox can stay null and nothing else would erase the live rect.
    // croppedReferenceImage swaps the canvas between the original and cropped bitmaps.
    // roiReferenceImage must stay OUT of these deps: the regen effect below owns that trigger, and
    // adding it here would flash the full original mid-navigation while a crop is held on screen.
    useEffect(() => {
        setupCanvas()
    }, [roiRectangles, showROI, zoom, pendingCropBox, cropBoxDimensions, mode, isDrawingCrop, croppedReferenceImage]);

    useEffect(() => {
        // imageSrc changes in place on next/prev (incl. arrow keys, which navigate in any mode)
        // and on HD arrival. The committed crop DELIBERATELY survives both: navigation re-applies
        // it to each image on the fly (seek-with-crop), HD arrival re-applies it at HD resolution.
        // Only the in-progress gesture is dropped. The outgoing cropped bitmap is also kept — it
        // stays displayed until the incoming image's crop is ready, then gets replaced (no flash
        // of the full image, and the old bitmap is freed by that replacement).
        setPendingCropBox(null);
        setIsDrawingCrop(false);
        return loadImage();
    }, [props.imageSrc, props.show]);

    // A new original landed (navigation, HD arrival, initial open). With a committed crop,
    // re-apply it to the new bitmap on the fly; otherwise paint the plain original (this
    // replaces the imperative paint that used to live in loadImage's onload).
    // Deps are [roiReferenceImage] ONLY: adding cropBoxDimensions would double-generate on
    // every Save Crop, since saveCrop already generates directly.
    useEffect(() => {
        if (!roiReferenceImage) {
            return;
        }
        // Superseded mid-flight: the user navigated again before this original landed, so it is
        // no longer the image the popup wants — skip the synchronous encode (or paint) entirely;
        // the current image's own load is already in flight and will trigger this effect itself.
        // Data-URL src round-trips verbatim, so the compare is exact (same idiom as the
        // enterCropMode and copy guards).
        if (roiReferenceImage.src !== props.imageSrc) {
            return;
        }
        if (cropBoxDimensions) {
            cropNavTimingRef.current = performance.now();
            const generateStart = performance.now();
            generateCroppedImage(cropBoxDimensions, roiReferenceImage);
            console.log(`[crop-nav] generate (crop+encode) took ${Math.round(performance.now() - generateStart)}ms`);
        } else {
            setupCanvas(roiReferenceImage);
        }
    }, [roiReferenceImage]);

    // Decode the applied crop for canvas drawing (mirrors loadImage for the original)
    useEffect(() => {
        if (!croppedImageSrc) {
            setCroppedReferenceImage(null);
            return;
        }
        // Cancellation covers rapid re-saves (out-of-order decodes) and navigation/reset mid-decode
        let cancelled = false;
        const image = new Image();
        image.src = croppedImageSrc;
        image.onload = () => {
            if (cancelled) {
                return;
            }
            setCroppedReferenceImage(image);
            setupCanvas(image, true);
            if (cropNavTimingRef.current !== null) {
                console.log(`[crop-nav] cropped image displayed ${Math.round(performance.now() - cropNavTimingRef.current)}ms after new original was ready`);
                cropNavTimingRef.current = null;
            }
        };
        return () => {
            cancelled = true;
        };
    }, [croppedImageSrc]);

    // Create Video: the video ROI survives popup sessions server-side. Fetch it on open so the
    // popup shows the stored crop the same way a just-saved one shows. A full-frame ROI means
    // "no crop" and seeds nothing. Fire-and-forget failures: the popup just opens uncropped.
    useEffect(() => {
        if (!props.show || props.galleryName !== 'Create Video') {
            return;
        }
        let cancelled = false;
        const fetchStoredVideoRoi = async () => {
            try {
                const idResponse = await videoCreationApi.getVideoCreationImageSetID(locationInfo.id, captureDate);
                if (cancelled || !idResponse?.vid_creation_img_set_id) {
                    return;
                }
                const roi = await videoCreationApi.getRoiForVideoImageSet(idResponse.vid_creation_img_set_id);
                if (cancelled || !isValidRelativeBox(roi) || isFullFrameBox(roi)) {
                    return;
                }
                setFetchedVideoRoi(roi);
            } catch (e) {
                console.error('Error fetching stored video crop ROI', e);
            }
        };
        fetchStoredVideoRoi();
        return () => {
            cancelled = true;
        };
    }, [props.show, props.galleryName]);

    // Apply the fetched ROI once an original for the CURRENT image is decoded, whichever lands
    // first — fetch or image. The nav regen effect can't do this (its deps deliberately exclude
    // cropBoxDimensions), so the seed generates directly, saveCrop-style. Consumed exactly once;
    // a crop the user committed before the fetch resolved wins and the fetch result is dropped.
    useEffect(() => {
        if (!fetchedVideoRoi || !roiReferenceImage) {
            return;
        }
        if (roiReferenceImage.src !== props.imageSrc) {
            return; // superseded mid-navigation; the incoming image re-triggers this effect
        }
        if (!cropBoxDimensions) {
            setCropBoxDimensions(fetchedVideoRoi);
            generateCroppedImage(fetchedVideoRoi, roiReferenceImage);
        }
        setFetchedVideoRoi(null);
    }, [fetchedVideoRoi, roiReferenceImage, cropBoxDimensions]);

    const setupCanvas = (bgImage?: HTMLImageElement, bgIsCropped?: boolean) => {
        const canvas: any = canvasRef.current;
        if (!canvas) {
            return;
        }
        const canvasBgImage = bgImage ? bgImage : (croppedReferenceImage || roiReferenceImage)
        // Callers that pass bgImage must say whether it is the cropped bitmap: their closures can
        // hold stale cropped state (loadImage's onload after navigation, the crop decode onload
        // before its setState commits), so deriving this from state would misfire for a frame.
        const isCroppedView = bgImage ? !!bgIsCropped : !!croppedReferenceImage;
        if (!canvasBgImage) {
            return;
        }
        const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
        const imageWidth = canvasBgImage.width;
        const imageHeight = canvasBgImage.height;

        canvas.width = imageWidth;
        canvas.height = imageHeight
        // This repaint runs on entering crop mode (mode is in its effect deps), so a plain "" here
        // would wipe the crosshair before the first mouse move
        canvas.style.cursor = mode === UserMode.CROP ? PointerType.CROSSHAIR : ""

        ctx.save()
        const minPanX = imageWidth / zoom - imageWidth
        const minPanY = imageHeight / zoom - imageHeight

        const absPanX = panX * imageWidth
        const absPanY = panY * imageHeight

        const maxPanX = Math.max(absPanX, minPanX)
        const maxPanY = Math.max(absPanY, minPanY)

        const translateX = Math.min(maxPanX, 0)
        const translateY = Math.min(maxPanY, 0)
        ctx.scale(zoom, zoom)
        ctx.translate(translateX, translateY)

        ctx.clearRect(0, 0, imageWidth, imageHeight);
        ctx.drawImage(canvasBgImage, 0, 0);
        // ROIs are original-image-relative — on the cropped canvas they would paint misplaced
        if (showROI && !isCroppedView) {
            roiRectangles.forEach((roiRect) => {
                const {
                    roiX,
                    roiY,
                    roiWidth,
                    roiHeight
                } = calculateAbsoluteROIValues(roiRect, imageWidth, imageHeight);
                ctx.strokeStyle = roiColor;
                ctx.lineWidth = calculateLineWidth(imageHeight)
                ctx.strokeRect(roiX, roiY, roiWidth, roiHeight);
            });
        }
        // Crop box (not an ROI, so not gated by showROI): in crop mode show the pending box under
        // inspection (displayed-image-relative — correct on either canvas); otherwise the committed
        // outline, but only on the ORIGINAL view — once the crop is applied the canvas IS the crop.
        const cropBoxToDraw = mode === UserMode.CROP ? pendingCropBox : (isCroppedView ? null : cropBoxDimensions);
        if (cropBoxToDraw) {
            const {
                roiX,
                roiY,
                roiWidth,
                roiHeight
            } = calculateAbsoluteROIValues(cropBoxToDraw, imageWidth, imageHeight);
            const cropLineWidth = calculateLineWidth(imageHeight);
            ctx.strokeStyle = cropBoxColor;
            ctx.lineWidth = cropLineWidth;
            ctx.setLineDash([cropLineWidth * 3, cropLineWidth * 2]);
            ctx.strokeRect(roiX, roiY, roiWidth, roiHeight);
            // setLineDash is sticky — clear it so later strokes on this context stay solid
            ctx.setLineDash([]);
        }
        ctx.restore()
    }

    const setRoiBorderColor = (rectangle: any, borderColor = roiColor) => {
        const canvas: any = canvasRef.current;
        if (!canvas || !rectangle || !showROI) {
            return;
        }

        const ctx: CanvasRenderingContext2D = canvas.getContext('2d');
        const {
            roiX,
            roiY,
            roiWidth,
            roiHeight
        } = calculateAbsoluteROIValues(rectangle, canvas.width, canvas.height);
        ctx.save();
        ctx.strokeStyle = borderColor;
        ctx.lineWidth = calculateLineWidth(canvas.height);
        ctx.strokeRect(roiX, roiY, roiWidth, roiHeight);
    }

    useEffect(() => {
        setRoiBorderColor(highlightedROIRectangle, 'red');
    }, [highlightedROIRectangle]);

    const handleMouseDown = (event: any) => {
        if (mode === UserMode.VIEW) {
            return;
        }
        const {offsetX, offsetY} = event.nativeEvent;
        const canvas: any = canvasRef.current;
        const relStartX = roundOff((offsetX / canvas.getBoundingClientRect().width))
        const relStartY = roundOff((offsetY / canvas.getBoundingClientRect().height))

        if (mode === UserMode.ZOOM_PAN) {
            if (zoom <= 1) {
                return;
            }
            setIsPanning(true);
            setPrevPanX(relStartX);
            setPrevPanY(relStartY);
            return;
        }

        if (mode === UserMode.CROP) {
            // Starting a new gesture replaces any box awaiting inspection
            setPendingCropBox(null);
            setIsDrawingCrop(true);
            setStartX(relStartX);
            setStartY(relStartY);
            // Init the drag offsets to the start point, NOT 0 like DRAW: crop normalizes with
            // min/abs, so a zero/stale offset would paint a phantom rect from (0,0) on the first
            // move. DRAW only escapes that because its negative-clamp collapses it to zero size.
            setDragOffsetX(relStartX);
            setDragOffsetY(relStartY);
            // Return before the ROI hit-testing below — it would clobber CROP into DRAW/MOVE/RESIZE
            return;
        }

        const {position: pointerPosition, roi: highlightedROI} = getRoiAtPointerPosition(relStartX, relStartY)
        const drawMode = getModeForCursorPosition(pointerPosition)

        setHighlightedROIRectangle(_ => highlightedROI)
        setMode(_ => getModeForCursorPosition(pointerPosition));
        setResizingDirection(_ => pointerPosition);

        if (drawMode === UserMode.DRAW) {
            setStartX(relStartX);
            setStartY(relStartY);
            setDragOffsetX(0)
            setDragOffsetY(0)
        }

        if (drawMode === UserMode.MOVE) {
            if (!highlightedROI) {
                return;
            }
            setRoiRectangles(prevRoiRectangles => {
                const idx = prevRoiRectangles.findIndex(roi => {
                    return (roi.relative_x === highlightedROI.relative_x &&
                        roi.relative_y === highlightedROI.relative_y &&
                        roi.relative_width === highlightedROI.relative_width &&
                        roi.relative_height === highlightedROI.relative_height);
                })
                if (idx !== -1) {
                    prevRoiRectangles.splice(idx, 1);
                }
                return [...prevRoiRectangles];
            });

            setDragOffsetX(roundOff(relStartX - highlightedROI.relative_x));
            setDragOffsetY(roundOff(relStartY - highlightedROI.relative_y));

        }
        if (drawMode === UserMode.RESIZE) {
            if (!highlightedROI) {
                return;
            }
            // Remove active rect from list of rectangle. Will be added back once resizing is complete
            const idx = roiRectangles.findIndex(roi => {
                return (roi.relative_x === highlightedROI.relative_x &&
                    roi.relative_y === highlightedROI.relative_y &&
                    roi.relative_width === highlightedROI.relative_width &&
                    roi.relative_height === highlightedROI.relative_height);
            })
            if (idx >= 0) {
                roiRectangles.splice(idx, 1);
            }
            setRoiRectangles(_ => [...roiRectangles]);
        }
    }

    const handleMouseMove = (event: any) => {
        if (mode === UserMode.VIEW) {
            return;
        }
        const {offsetX, offsetY} = event.nativeEvent;
        const canvas: any = canvasRef.current;
        const relDragOffsetX = roundOff((offsetX / canvas.getBoundingClientRect().width))
        const relDragOffsetY = roundOff((offsetY / canvas.getBoundingClientRect().height))

        if (mode === UserMode.ZOOM_PAN) {
            if (isPanning) {
                const deltaX = relDragOffsetX - prevPanX;
                const deltaY = relDragOffsetY - prevPanY;
                setPanX((prevPanX) => prevPanX + deltaX);
                setPanY((prevPanY) => prevPanY + deltaY);
                setPrevPanX(relDragOffsetX);
                setPrevPanY(relDragOffsetY);
                setupCanvas();
            }
            return;
        }

        if (mode === UserMode.CROP) {
            canvas.style.cursor = PointerType.CROSSHAIR;
            if (isDrawingCrop) {
                setDragOffsetX(relDragOffsetX);
                setDragOffsetY(relDragOffsetY);
                redrawCanvas();
            }
            // Return before the ROI hit-test — crop must never highlight/move/resize regions
            return;
        }

        // Check if pointer is over any rectangle
        const {position: pointerPosition, roi: highlightedROI} = getRoiAtPointerPosition(relDragOffsetX, relDragOffsetY)
        canvas.style.cursor = getCursorForPointerPosition(pointerPosition);

        // For any mode other than move, update
        if (mode === UserMode.HOVER) {
            setHighlightedROIRectangle(prevRoI => {
                if (!highlightedROI) {
                    // Reset color changes to previous rectangle
                    setRoiBorderColor(prevRoI);
                }
                return highlightedROI
            });
        }

        if (mode === UserMode.DRAW) {
            canvas.style.cursor = PointerType.CROSSHAIR;
            setDragOffsetX(relDragOffsetX);
            setDragOffsetY(relDragOffsetY);
            redrawCanvas();
        }

        if (mode === UserMode.MOVE) {
            if (!highlightedROIRectangle) {
                return
            }

            canvas.style.cursor = PointerType.MOVE;
            const activeRect = {
                ...highlightedROIRectangle,
                relative_x: roundOff(relDragOffsetX - dragOffsetX),
                relative_y: roundOff(relDragOffsetY - dragOffsetY)
            }
            // Update selected
            setHighlightedROIRectangle(_ => activeRect);
            redrawCanvas();
        }

        if (mode === UserMode.RESIZE) {
            if (!highlightedROIRectangle) {
                return
            }
            canvas.style.cursor = getCursorForPointerPosition(resizingDirection);
            const resizedROI = getResizedCoordinates(relDragOffsetX, relDragOffsetY, resizingDirection, highlightedROIRectangle);
            setHighlightedROIRectangle(_ => resizedROI);
            redrawCanvas();
        }

    }

    const handleMouseUp = (event: any) => {
        setIsPanning(false);
        if ([UserMode.VIEW, UserMode.ZOOM_PAN].includes(mode)) {
            return;
        }
        const {offsetX, offsetY} = event.nativeEvent;
        const canvas: any = canvasRef.current;
        const relEndX = roundOff(((offsetX / canvas.getBoundingClientRect().width) - startX))
        const relEndY = roundOff(((offsetY / canvas.getBoundingClientRect().height) - startY))

        if (mode === UserMode.CROP) {
            if (!isDrawingCrop) {
                // Stray release without a drag (crop mode is entered by button/key, not pointerdown)
                return;
            }
            setIsDrawingCrop(false);

            // relEndX/relEndY above are deltas from the start point; crop needs the raw fractions
            const rect = canvas.getBoundingClientRect();
            let endX = offsetX / rect.width;
            let endY = offsetY / rect.height;

            // Create Video: the box IS the video's crop framing, so it locks to the fixed
            // 16:9 video ratio (see constrainCropEndToVideoAR)
            if (props.galleryName === 'Create Video') {
                ({endX, endY} = constrainCropEndToVideoAR(startX, startY, endX, endY, canvas.width, canvas.height));
            }

            // Normalize so drags in any direction store a positive-size box
            const cropStartX = Math.min(startX, endX);
            const cropStartY = Math.min(startY, endY);
            const cropWidth = Math.abs(endX - startX);
            const cropHeight = Math.abs(endY - startY);

            // Accidental click / degenerate box — ignore and stay in crop mode to retry
            if (cropWidth < 0.005 || cropHeight < 0.005) {
                return;
            }

            // Screen fractions -> image-relative fractions via the visible sub-rectangle of the
            // image. getZoomBoxDimensions clamps identically to setupCanvas's transform, so the
            // stored box matches what was on screen at any zoom/pan (identity at zoom 1).
            const zoomBox = getZoomBoxDimensions(zoom, panX, panY, canvas.width, canvas.height);
            setPendingCropBox({
                relative_x: roundOff((zoomBox.x + cropStartX * zoomBox.width) / canvas.width),
                relative_y: roundOff((zoomBox.y + cropStartY * zoomBox.height) / canvas.height),
                relative_width: roundOff((cropWidth * zoomBox.width) / canvas.width),
                relative_height: roundOff((cropHeight * zoomBox.height) / canvas.height)
            });
            // Stay in CROP mode so the user can inspect, then Save Crop or Cancel. The repaint
            // effect draws the pending box; an imperative redraw here would use stale closure state.
            return;
        }

        if (mode === UserMode.DRAW) {
            setRoiRectangles(prev => [
                ...prev,
                {
                    relative_x: startX,
                    relative_y: startY,
                    relative_width: relEndX,
                    relative_height: relEndY
                }
            ]);
            redrawCanvas();
        }

        if ([UserMode.MOVE, UserMode.RESIZE].includes(mode)) {
            if (!highlightedROIRectangle) {
                return;
            }
            roiRectangles.push({...highlightedROIRectangle})
            setRoiRectangles(_ => [...roiRectangles]);
            redrawCanvas();
        }

        // Reset
        setShowROI(true);
        setMode(UserMode.HOVER);
        setHighlightedROIRectangle(null);
        setResizingDirection(_ => null);
    };

    const redrawCanvas = () => {
        // Redraw all previous rectangles
        setupCanvas();

        const canvas: any = canvasRef.current;
        const ctx: CanvasRenderingContext2D = canvas.getContext('2d');

        if (mode === UserMode.DRAW) {
            const diffWidth = roundOff((dragOffsetX - startX));
            const diffHeight = roundOff((dragOffsetY - startY));

            const roiRect: ROIRectangle = {
                relative_x: startX,
                relative_y: startY,
                relative_width: diffWidth <= 0 ? 0 : diffWidth,
                relative_height: diffHeight <= 0 ? 0 : diffHeight
            }
            const {
                roiX,
                roiY,
                roiWidth,
                roiHeight
            } = calculateAbsoluteROIValues(roiRect, canvas.width, canvas.height);

            ctx.strokeStyle = roiColor;
            ctx.lineWidth = calculateLineWidth(canvas.height);
            ctx.beginPath();
            ctx.rect(roiX, roiY, roiWidth, roiHeight);
            ctx.stroke();
        }

        if ([UserMode.MOVE, UserMode.RESIZE].includes(mode)) {
            const {
                roiX,
                roiY,
                roiWidth,
                roiHeight
            } = calculateAbsoluteROIValues(highlightedROIRectangle, canvas.width, canvas.height);

            ctx.strokeStyle = 'red';
            ctx.lineWidth = calculateLineWidth(canvas.height);
            ctx.beginPath();
            ctx.rect(roiX, roiY, roiWidth, roiHeight);
            ctx.stroke();
        }

        if (mode === UserMode.CROP && isDrawingCrop) {
            // Live crop rect: like the DRAW branch, drawn untransformed in device space (screen
            // fraction x canvas size tracks the cursor at any zoom), but normalized with min/abs
            // so up/left drags work — DRAW's negative-clamp is deliberately not copied.
            // Create Video: constrain the live rect exactly like mouse-up will, so what the user
            // sees while dragging is the aspect-locked box that gets stored.
            let endDragX = dragOffsetX;
            let endDragY = dragOffsetY;
            if (props.galleryName === 'Create Video') {
                ({endX: endDragX, endY: endDragY} = constrainCropEndToVideoAR(startX, startY, dragOffsetX, dragOffsetY, canvas.width, canvas.height));
            }
            const x = Math.min(startX, endDragX) * canvas.width;
            const y = Math.min(startY, endDragY) * canvas.height;
            const w = Math.abs(endDragX - startX) * canvas.width;
            const h = Math.abs(endDragY - startY) * canvas.height;
            const cropLineWidth = calculateLineWidth(canvas.height);
            // save/restore is REQUIRED here: setLineDash is sticky context state that setupCanvas
            // never resets, so an unscoped dash would turn every ROI dashed on later frames
            ctx.save();
            ctx.strokeStyle = cropBoxColor;
            ctx.lineWidth = cropLineWidth;
            ctx.setLineDash([cropLineWidth * 3, cropLineWidth * 2]);
            ctx.strokeRect(x, y, w, h);
            ctx.restore();
        }
    };

    const nearPoint = (cursorX: number, cursorY: number, roiX: number, roiY: number, name: string, max_distance = 0.005) => {
        return Math.abs(cursorX - roiX) < max_distance && Math.abs(cursorY - roiY) < max_distance ? name : null;
    };

    const distance = (a: any, b: any) => roundOff(Math.sqrt(Math.pow(a.x - b.x, 2) + Math.pow(a.y - b.y, 2)));

    const nearLine = (cursorX: number, cursorY: number, x1: number, y1: number, x2: number, y2: number, maxDistance = 0.001) => {
        const a = {x: x1, y: y1};
        const b = {x: x2, y: y2};
        const c = {x: cursorX, y: cursorY};
        const offset = distance(a, b) - (distance(a, c) + distance(b, c));
        return Math.abs(offset) < maxDistance;
    };

    const getCursorPositionWithinRoi = (cursorX: number, cursorY: number, roi: ROIRectangle) => {
        if (!roi) {
            return null;
        }
        const top = nearLine(cursorX, cursorY, roi.relative_x, roi.relative_y, roi.relative_x + roi.relative_width, roi.relative_y) ? "top" : null;
        const bottom = nearLine(cursorX, cursorY, roi.relative_x, roi.relative_y + roi.relative_height, roi.relative_x + roi.relative_width, roi.relative_y + roi.relative_height) ? "bottom" : null;
        const left = nearLine(cursorX, cursorY, roi.relative_x, roi.relative_y, roi.relative_x, roi.relative_y + roi.relative_height) ? "left" : null;
        const right = nearLine(cursorX, cursorY, roi.relative_x + roi.relative_width, roi.relative_y, roi.relative_x + roi.relative_width, roi.relative_y + roi.relative_height) ? "right" : null;
        const topLeft = nearPoint(cursorX, cursorY, roi.relative_x, roi.relative_y, "tl");
        const topRight = nearPoint(cursorX, cursorY, roi.relative_x + roi.relative_width, roi.relative_y, "tr");
        const bottomLeft = nearPoint(cursorX, cursorY, roi.relative_x, roi.relative_y + roi.relative_height, "bl");
        const bottomRight = nearPoint(cursorX, cursorY, roi.relative_x + roi.relative_width, roi.relative_y + roi.relative_height, "br");
        const inside = cursorX >= roi.relative_x && cursorX <= roi.relative_x + roi.relative_width && cursorY >= roi.relative_y && cursorY <= roi.relative_y + roi.relative_height ? "inside" : null;
        return top || left || right || bottom || topLeft || topRight || bottomLeft || bottomRight || inside;
    }

    const getRoiAtPointerPosition = (clientX: number, clientY: number): {
        position: string | null,
        roi: ROIRectangle | null
    } => {
        let position = null;
        let roi = null;
        for (const el of roiRectangles) {
            position = getCursorPositionWithinRoi(clientX, clientY, el);
            if (position !== null) {
                roi = {...el};
                break
            }
        }
        return {position, roi}
    }

    const saveRegionsOfInterest = () => {
        setMode(UserMode.VIEW);
        props.onSaveRegionsOfInterest(roiRectangles)
        const canvas: any = canvasRef.current;
        canvas.style.cursor = "";
    };

    const clearAllRectangles = () => {
        setRoiRectangles(_ => [])
        redrawCanvas();
    };

    const onCancelDrawRoi = () => {
        setMode(UserMode.VIEW);
        const canvas: any = canvasRef.current;
        canvas.style.cursor = "";
    }

    const enterCropMode = () => {
        // A crop is committed but its bitmap is still decoding: a box drawn against the outgoing
        // bitmap would compose against the NEW committed crop — wrong coords. The window is tens
        // of ms; just ignore the click. (Data-URL src round-trips verbatim, so compare is exact.)
        if (croppedImageSrc && croppedReferenceImage?.src !== croppedImageSrc) {
            return;
        }
        // Defensive resets; the previously SAVED crop is deliberately untouched — state only
        // changes on Save Crop, so a cancelled re-crop keeps the earlier crop intact
        setPendingCropBox(null);
        setIsDrawingCrop(false);
        setIsPanning(false);
        setMode(UserMode.CROP);
    }

    const exitCropMode = () => {
        setIsDrawingCrop(false);
        // Same rule as onZoomOut: zoomed sessions return to ZOOM_PAN, otherwise VIEW
        setMode(zoom > 1 ? UserMode.ZOOM_PAN : UserMode.VIEW);
        const canvas: any = canvasRef.current;
        if (canvas) {
            canvas.style.cursor = "";
        }
    }

    // Restore the plain original view after a failed generation. Clearing state alone is not
    // enough: when croppedImageSrc was already null, no dep changes and nothing repaints — the
    // PREVIOUS image would stay stuck on screen (a failure mode navigation persistence adds).
    const abandonCropGeneration = (sourceImage: HTMLImageElement) => {
        setCroppedImageSrc(null);
        setCroppedReferenceImage(null);
        cropNavTimingRef.current = null;    // don't inflate the next navigation's timing log
        setupCanvas(sourceImage);           // explicit bgImage — immune to stale closure state
    }

    // Slice cropBox out of sourceImage — always an ORIGINAL full-resolution bitmap, never a
    // previous crop, so repeated narrowing has zero generational quality loss. sourceImage is an
    // explicit argument because the navigation-regen effect and saveCrop both call this in ticks
    // where reading roiReferenceImage from the closure could be stale. RAM only: the result goes
    // into croppedImageSrc state and nowhere else.
    const generateCroppedImage = (cropBox: ROIRectangle, sourceImage: HTMLImageElement) => {
        const w = sourceImage.width;
        const h = sourceImage.height;
        // Clamp so the independently-rounded x and width can't overshoot the image edges
        const sourceX = Math.min(w - 1, Math.max(0, Math.round(cropBox.relative_x * w)));
        const sourceY = Math.min(h - 1, Math.max(0, Math.round(cropBox.relative_y * h)));
        const sourceW = Math.max(1, Math.min(Math.round(cropBox.relative_width * w), w - sourceX));
        const sourceH = Math.max(1, Math.min(Math.round(cropBox.relative_height * h), h - sourceY));
        const offscreen = document.createElement('canvas');
        offscreen.width = sourceW;
        offscreen.height = sourceH;
        const offscreenCtx = offscreen.getContext('2d');
        if (!offscreenCtx) {
            abandonCropGeneration(sourceImage);
            return;
        }
        offscreenCtx.drawImage(sourceImage, sourceX, sourceY, sourceW, sourceH, 0, 0, sourceW, sourceH);
        try {
            // Keep the source's mime (regex handles data:image/jpeg;base64,... and data:image/png,...);
            // toDataURL silently falls back to PNG for types it can't encode
            const mimeMatch = props.imageSrc.match(/^data:(image\/[a-z0-9.+-]+)[;,]/i);
            setCroppedImageSrc(offscreen.toDataURL(mimeMatch ? mimeMatch[1] : 'image/png', 0.92));
        } catch (e) {
            // Not expected with data-URL sources (no canvas taint); degrade to the un-cropped view
            console.error('Error generating cropped image', e);
            onShowToastMessage?.('Could not generate the cropped image');
            abandonCropGeneration(sourceImage);
        }
    }

    // Shared tail of both save paths: commit the composed box and apply the visual crop.
    // composed/sourceImage are arguments — cropBoxDimensions state is stale this tick, and
    // saveVideoCrop calls this after an await where closure state may lag a navigation.
    const commitCrop = (composed: ROIRectangle, sourceImage: HTMLImageElement) => {
        setCropBoxDimensions(composed);
        generateCroppedImage(composed, sourceImage);
        setPendingCropBox(null);
        setIsDrawingCrop(false);
        // Crop replaces zooming: back to VIEW at zoom 1, pan 0 (cursor clears via the repaint)
        onResetZoom();
    }

    const saveCrop = () => {
        if (!pendingCropBox || !roiReferenceImage) {
            return;
        }
        // pendingCropBox is displayed-image-relative; compose into original-image fractions
        const composed = cropBoxDimensions
            ? composeCropBoxes(cropBoxDimensions, pendingCropBox)
            : pendingCropBox;
        commitCrop(composed, roiReferenceImage);
    }

    // Store a box as this day/location's video image set ROI — the crop framing the video
    // render uses. Failures toast and return false so callers leave the view untouched.
    const storeVideoRoi = async (roi: ROIRectangle): Promise<boolean> => {
        try {
            const idResponse = await videoCreationApi.getVideoCreationImageSetID(locationInfo.id, captureDate);
            if (!idResponse?.vid_creation_img_set_id) {
                onShowToastMessage?.('No video image set for this day — assign video images first', 'danger');
                return false;
            }
            const roiResponse = await videoCreationApi.setRoiForVideoImageSet(idResponse.vid_creation_img_set_id, roi);
            if (!roiResponse || roiResponse.error || !roiResponse.region_of_interest) {
                onShowToastMessage?.('Could not save the video crop', 'danger');
                return false;
            }
            return true;
        } catch (e) {
            console.error('Error storing video crop ROI', e);
            onShowToastMessage?.('Could not save the video crop', 'danger');
            return false;
        }
    }

    // Create Video's save: persist the ROI FIRST, then apply the same visual crop as saveCrop —
    // the preview never shows a crop the backend doesn't hold. If the user navigates during the
    // store round-trip, the committed box still lands (it's image-set-wide, not per-image) and
    // the worst case is the crop bitmap lagging one image until the next navigation repaints.
    const saveVideoCrop = async () => {
        if (!pendingCropBox || !roiReferenceImage) {
            return;
        }
        const composed = cropBoxDimensions
            ? composeCropBoxes(cropBoxDimensions, pendingCropBox)
            : pendingCropBox;
        if (!(await storeVideoRoi(composed))) {
            return;
        }
        commitCrop(composed, roiReferenceImage);
        onShowToastMessage?.('Video crop saved', 'success');
    }

    // Create Video's reset: store full-frame ("no crop") so the backend matches the restored
    // view, then clear the visual crop. On failure the view is left as-is — screen and stored
    // state must never silently diverge.
    const resetVideoCrop = async () => {
        if (!(await storeVideoRoi(FULL_FRAME_VIDEO_ROI))) {
            return;
        }
        resetCrop();
        onShowToastMessage?.('Video crop reset to full frame', 'success');
    }

    const cancelCrop = () => {
        setPendingCropBox(null);
        exitCropMode();
    }

    const resetCrop = () => {
        // Drop the RAM copy and the committed coords — back to the untouched original
        setCroppedImageSrc(null);
        setCroppedReferenceImage(null);
        setCropBoxDimensions(null);
        setPendingCropBox(null);
        onResetZoom();
    }

    // PrintScreen emulation shared by copy and download-while-cropped: the FULL clean crop
    // scaled to the preview's displayed pixel size (rect x devicePixelRatio, both read at
    // click time), with the date bar drawn at true screen metrics when shown. Zoom never
    // affects this — zoom is an internal canvas transform, the element's rect is invariant,
    // and we always draw the whole bitmap.
    const buildCropScreenshotCanvas = (): HTMLCanvasElement | null => {
        // Same flight guard as before: only a settled, decoded crop is exportable
        if (!croppedReferenceImage || croppedReferenceImage.src !== croppedImageSrc) {
            return null;
        }
        const canvas: any = canvasRef.current;
        if (!canvas) {
            return null;
        }
        const rect = canvas.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        const out = document.createElement('canvas');
        out.width = Math.max(1, Math.round(rect.width * dpr));
        out.height = Math.max(1, Math.round(rect.height * dpr));
        const ctx = out.getContext('2d');
        if (!ctx) {
            return null;
        }
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(croppedReferenceImage, 0, 0, out.width, out.height);
        if (cropDateBarAllowedForGallery && showCropDateBar) {
            drawDateBarAtScreenMetrics(ctx, out.width, out.height, dpr, props.imageInfo.captured_at, showLogoInDateOverlay);
        }
        return out;
    }

    const copyCroppedImageToClipboard = async () => {
        // What lands on the clipboard is the preview at its displayed size, date bar included
        // when shown — not the native-resolution clean crop (that stays in RAM untouched)
        const out = buildCropScreenshotCanvas();
        if (!out) {
            return;
        }
        try {
            // PNG is the only image type Chromium's async clipboard broadly accepts
            const blob: Blob = await new Promise<Blob>((resolve, reject) =>
                out.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png'));
            await navigator.clipboard.write([new ClipboardItem({'image/png': blob})]);
            onShowToastMessage?.('Cropped image copied to clipboard', 'success');
        } catch (e) {
            // Typical cause: non-HTTPS origin — the async clipboard needs a secure context.
            // Right-click > Copy image on the canvas still works there.
            console.error('Error copying cropped image', e);
            onShowToastMessage?.('Could not copy — the browser blocked clipboard access', 'danger');
        }
    }

    // Download-while-cropped: same screenshot artifact as copy, saved as a file. The
    // un-cropped download path (the original file via download_url) is untouched.
    const downloadCropScreenshot = async (event: any) => {
        // CustomLink forwards onClick to a router Link, which honors preventDefault —
        // stops the to="#" navigation
        event.preventDefault();
        const out = buildCropScreenshotCanvas();
        if (!out) {
            return;
        }
        try {
            const blob: Blob = await new Promise<Blob>((resolve, reject) =>
                out.toBlob(b => b ? resolve(b) : reject(new Error('toBlob failed')), 'image/png'));
            const blobUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = blobUrl;
            // e.g. ..._sd.jpg -> ..._sd_cropped.png; extension-less names degrade to a plain append
            anchor.download = getImageFilename(props.imageInfo).replace(/\.[^.]+$/, '') + '_cropped.png';
            anchor.click();
            // Same immediate-revoke lifecycle as ImageGallery.downloadAlbum
            URL.revokeObjectURL(blobUrl);
        } catch (e) {
            console.error('Error downloading cropped image', e);
            onShowToastMessage?.('Error preparing cropped image download', 'danger');
        }
    }

    const onZoomIn = () => {
        setMode(UserMode.ZOOM_PAN)
        setZoom(prevState => prevState * 1.2)
    }

    const onZoomOut = () => {
        setZoom(prevState => {
            const newScale = prevState / 1.2
            if (newScale <= 1) {
                resetZoom();
                return 1;
            }
            setMode(UserMode.ZOOM_PAN)
            return newScale
        })
    }

    const resetZoom = () => {
        setPanX(0)
        setPanY(0)
        setupCanvas()
        setMode(UserMode.VIEW)
    };

    const onResetZoom = () => {
        resetZoom();
        setZoom(1);
    }


    /**
     * Calculates the position and dimensions of the zoom box.
     * @param zoom - The current zoom level.
     * @param panX - The relative horizontal pan offset.
     * @param panY - The relative vertical pan offset.
     * @param imageWidth - The original width of the image.
     * @param imageHeight - The original height of the image.
     * @returns An object with the x, y, width, and height of the zoom box.
     */
    const getZoomBoxDimensions = (zoom: number, panX: number, panY: number, imageWidth: number, imageHeight: number) => {
        // 1. Calculate width and height
        const zoomBoxWidth = imageWidth / zoom;
        const zoomBoxHeight = imageHeight / zoom;

        // 2. Calculate the final, clamped translation values
        const absolutePanX = panX * imageWidth;
        const maxNegativePanX = zoomBoxWidth - imageWidth;
        const clampedTranslateX = Math.min(Math.max(absolutePanX, maxNegativePanX), 0);

        const absolutePanY = panY * imageHeight;
        const maxNegativePanY = zoomBoxHeight - imageHeight;
        const clampedTranslateY = Math.min(Math.max(absolutePanY, maxNegativePanY), 0);

        // 3. The position is the inverse of the translation
        const zoomBoxX = -clampedTranslateX;
        const zoomBoxY = -clampedTranslateY;

        let zoomBoxDimensions = {
            x: zoomBoxX,
            y: zoomBoxY,
            width: zoomBoxWidth,
            height: zoomBoxHeight,
        };
        return zoomBoxDimensions
    }



    // Example usage inside the component:
    // const { x, y, width, height } = getZoomBox(zoom, panX, panY, canvas.width, canvas.height);
    // console.log(`Zoom box is at (${x}, ${y}) with dimensions ${width}x${height}`);

    const areWeOnApageWhereSelectRibbonShouldShow = () => {
        return caseImagesAreWeOnApageWhereSelectRibbonShouldShow(props.galleryName)
    }

    const getPopupImageCornerRibbonMarkupConditionally = (imageId: number) => {
        if (areWeOnApageWhereSelectRibbonShouldShow() && props.isSelect){
            return getImageCornerRibbonMarkup()
        } else if (props.galleryName == 'Create Video' && isImageInCreateVideoImageSet(imageId)) {
            return getImageCornerRibbonMarkup('For Video');
        }
        return null;
    }


    return (
        <React.Fragment>
            <Modal
                show={props.show}
                onHide={props.onHide}
                dialogClassName="modal-dialog-centered modal-full-width">
                <div className="carousel-inner ribbon-box">
                    { 
                        getPopupImageCornerRibbonMarkupConditionally(props.imageInfo.id)
                    }
                    <canvas
                        id="roiCanvas"
                        className="roi-canvas"
                        ref={canvasRef}
                        onPointerDown={handleMouseDown}
                        onPointerUp={handleMouseUp}
                        onPointerMove={handleMouseMove}>
                        <span>Could not load image</span>
                    </canvas>
                    {/* Two visibility regimes for the live date bar: cropped views are governed by
                        the new showCropDateBar toggle (hidden during CROP drawing — this div sets
                        no pointer-events and would swallow drags in the bottom strip); un-cropped
                        views keep the original zoomed-overlay behavior verbatim. */}
                    {(croppedImageSrc
                        ? (cropDateBarAllowedForGallery && showCropDateBar && mode !== UserMode.CROP)
                        : (mode === UserMode.ZOOM_PAN && showDateOverlay)) &&
                        <div className="popup-date-overlay">
                            <span>{getLocaleDateString(props.imageInfo.captured_at)},</span>
                            <span>{getTimeIn12h(props.imageInfo.captured_at)},</span>
                            <span>{getWeekDay(props.imageInfo.captured_at)}</span>
                            {/* Shown on zoomed AND cropped views alike - the crop-export painter
                                draws the same logo at the same screen metrics, so WYSIWYG holds */}
                            {showLogoInDateOverlay &&
                                <span className="popup-image-overlay-text-logo"><img src={cc_dark_bg_logo} /></span>
                            }
                        </div>
                    }
                    {mode === UserMode.VIEW && (<React.Fragment>
                        <button
                            onClick={props.onPrev}
                            className="carousel-control-prev"
                            type="button"
                            data-bs-slide="prev">
                            <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                            <span className="visually-hidden">Previous</span>
                        </button>
                        <button
                            onClick={props.onNext}
                            className="carousel-control-next"
                            type="button"
                            data-bs-slide="next">
                            <span className="carousel-control-next-icon" aria-hidden="true"></span>
                            <span className="visually-hidden">Next</span>
                        </button>
                    </React.Fragment>)
                    }
                </div>
                <Modal.Footer className="justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                        <span className="font-16">#{getMediaFileDisplayNameDependingOnContext(props.imageInfo, props.galleryName)}</span>
                        <span className="text-muted">{getTimeIn12h(props.imageInfo.captured_at)}</span>
                        {
                            <Form.Check className={classNames( "ms-3",mode === UserMode.ZOOM_PAN && !croppedImageSrc ? "visible": "invisible")}>
                                <Form.Switch>
                                    <Form.Check.Input
                                        defaultChecked={showDateOverlay}
                                        onClick={() => setShowDateOverlay(!showDateOverlay)}
                                        type="checkbox"
                                        role="switch"
                                        id="showOverlay"/>
                                    <Form.Check.Label
                                        htmlFor="showOverlay">Overlay Date on Zoomed Image
                                    </Form.Check.Label>
                                </Form.Switch>
                            </Form.Check>
                        }
                        {/* One switch for both contexts (same DOM node, so the uncontrolled
                            input can never desync): visible while zoomed with the date overlay
                            on, OR while a crop's date bar is showing */}
                        {
                            <Form.Check className={classNames( "ms-3",(mode === UserMode.ZOOM_PAN && showDateOverlay && !croppedImageSrc) || (croppedImageSrc && cropDateBarAllowedForGallery && showCropDateBar && mode !== UserMode.CROP) ? "visible": "invisible")}>
                                <Form.Switch>
                                    <Form.Check.Input
                                        defaultChecked={showLogoInDateOverlay}
                                        onClick={() => setShowLogoInDateOverlay(!showLogoInDateOverlay)}
                                        type="checkbox"
                                        role="switch"
                                        id="showLogoInOverlay"/>
                                    <Form.Check.Label
                                        htmlFor="showLogoInOverlay">Show Logo in Overlay?
                                    </Form.Check.Label>
                                </Form.Switch>
                            </Form.Check>
                        }
                    </div>
                    <div className="d-flex align-items-center image-popup-icon-container">
                        <CustomLink onClick={() => props.onHdRequested(props.imageIndex, props.imageInfo.id)} title="Request HD" to="#" className="image-popup-btn">
                            <span data-action="hd">
                                {props.imageInfo.hd_request_status === "PENDING" ? (
                                        <span className="hd-pending text-warning">
                                            <span className="material-symbols-outlined image-popup-icon">hd</span>
                                        </span>
                                    ) :
                                    (
                                        <span

                                            className={classNames("material-symbols-outlined image-popup-icon",
                                                {
                                                    "text-muted": props.imageInfo.hd_file_path === null,
                                                    "text-success": props.imageInfo.hd_file_path !== null
                                                })}>hd
                                        </span>
                                    )}
                            </span>
                        </CustomLink>
                        {/* Two branches, not one link with a conditional handler — the un-cropped
                            path must stay byte-for-byte untouched, and to/download differ anyway */}
                        {croppedImageSrc ? (
                            <CustomLink
                                title="Download Cropped Image"
                                className="image-popup-btn"
                                to="#"
                                onClick={downloadCropScreenshot}>
                                    <span className={classNames("material-symbols-outlined image-popup-icon",
                                        {
                                            "text-primary": croppedReferenceImage?.src === croppedImageSrc,
                                            "text-muted": croppedReferenceImage?.src !== croppedImageSrc
                                        })}>download</span>
                            </CustomLink>
                        ) : (
                            <CustomLink
                                title={props.galleryName === 'Video' ? "Download Video" : "Download Image"}
                                className="image-popup-btn"
                                to={props.imageInfo["download_url"]}
                                target="_target"
                                download={getImageFilename(props.imageInfo)}>
                                    <span className={classNames("material-symbols-outlined image-popup-icon",
                                        {
                                            "text-primary": props.imageInfo["download_url"] !== undefined,
                                            "text-muted": props.imageInfo["download_url"] === undefined
                                        })}>download</span>
                            </CustomLink>
                        )}


                        <CustomLink  title={!imageIsPrimaryCheckboxChecked(props.imageInfo.id, props.galleryName) ? "Select Image" : "De-select Image"} className="image-popup-btn">
                                        <span
                                            data-action="select"
                                            onClick={() => props.singleImageCheckboxHandler(props.imageInfo.id)}
                                            className={classNames("material-symbols-outlined image-popup-icon",
                                                {
                                                    "text-muted": !imageIsPrimaryCheckboxChecked(props.imageInfo.id, props.galleryName),
                                                    "text-success": imageIsPrimaryCheckboxChecked(props.imageInfo.id, props.galleryName)
                                                })}>{imageIsPrimaryCheckboxChecked(props.imageInfo.id, props.galleryName) ? "select_check_box" : "check_box_outline_blank"}
                                        </span>
                        </CustomLink>


                        <CustomLink  title={!props.isImageMarkedAsLocked(props.imageInfo.id) ? "Lock Image" : "Unlock Image"} className="image-popup-btn">
                                        <span
                                            data-action="lock"
                                            onClick={() => props.toggleMediaLockStatus(props.imageInfo.id)}
                                            className={classNames("material-symbols-outlined image-popup-icon lock_unlock_media_icon",
                                                {
                                                    "text-muted": !props.isImageMarkedAsLocked(props.imageInfo.id),
                                                    "text-success": props.isImageMarkedAsLocked(props.imageInfo.id)
                                                })}>{props.isImageMarkedAsLocked(props.imageInfo.id) ? "lock" : "lock_open"}
                                        </span>
                        </CustomLink>


                    </div>
                    <div className="d-flex align-items-center">
                        {[UserMode.ZOOM_PAN, UserMode.VIEW].includes(mode) &&
                            <React.Fragment>
                                <div className="image-popup-icon-container mt-2 me-3">

                                    <Button className="image-popup-btn" onClick={onZoomIn}>
                                        <span
                                            className="material-symbols-outlined image-popup-icon text-primary">zoom_in</span>
                                    </Button>
                                    <Button className="image-popup-btn" onClick={onZoomOut}>
                                        <span
                                            className="material-symbols-outlined image-popup-icon text-primary">zoom_out</span>
                                    </Button>
                                </div>
                            </React.Fragment>
                        }
                        {mode === UserMode.ZOOM_PAN &&
                            <Button
                                onClick={onResetZoom}
                                className="">Reset Zoom
                            </Button>
                        }
                        {![UserMode.VIEW, UserMode.ZOOM_PAN, UserMode.CROP].includes(mode) &&
                            <React.Fragment>
                                <Button
                                    onClick={clearAllRectangles}
                                    variant="secondary-outline">
                                    Clear All
                                </Button>
                                <Button
                                    onClick={onCancelDrawRoi}
                                    variant="secondary-outline">
                                    Cancel
                                </Button>
                                <Button
                                    onClick={saveRegionsOfInterest}
                                    variant="primary">
                                    Save Regions
                                </Button>
                            </React.Fragment>
                        }
                        {/* Hidden while a crop is applied: ROIs drawn on the cropped canvas would be
                            crop-relative and Save Regions would persist them to the backend as if
                            they were full-image coords */}
                        {mode === UserMode.VIEW && !croppedImageSrc &&
                            <Button
                                onClick={() => setMode(UserMode.HOVER)}
                                className="mr-3">Draw Regions
                            </Button>
                        }
                        {/* Also shown while zoomed (unlike Draw Regions) so cropping works mid-zoom.
                            Keep as a plain Button (no href/as) — the 'c' keybind relies on a real
                            <button> ignoring .click() while disabled.
                            Not rendered on the Create Video tab: that tab has its own crop button
                            below, running this same flow with the box locked to the fixed 16:9
                            video ratio and the result stored as the video ROI. Exactly one
                            [data-action="crop"] button mounts per tab, so the 'c' keybind always
                            hits the right one. */}
                        {props.galleryName !== 'Create Video' && [UserMode.VIEW, UserMode.ZOOM_PAN, UserMode.CROP].includes(mode) &&
                            <Button
                                data-action="crop"
                                onClick={mode === UserMode.CROP ? saveCrop : enterCropMode}
                                disabled={mode === UserMode.CROP && !pendingCropBox}
                                className="ms-1">
                                {mode === UserMode.CROP ? "Save Crop" : "Crop Image"}
                            </Button>
                        }
                        {mode === UserMode.CROP &&
                            <Button
                                data-action="crop-cancel"
                                onClick={cancelCrop}
                                variant="secondary-outline">
                                Cancel
                            </Button>
                        }
                        {/* data-action="crop-copy" is the Ctrl+C delegation target; hidden mid-re-crop
                            (Cancel brings both back) */}
                        {croppedImageSrc && mode !== UserMode.CROP &&
                            <Button
                                data-action="crop-copy"
                                onClick={copyCroppedImageToClipboard}
                                className="ms-1">
                                Copy Image
                            </Button>
                        }
                        {/* On Create Video the reset must also store a full-frame ROI, so the
                            backend's video framing follows the restored view */}
                        {croppedImageSrc && mode !== UserMode.CROP &&
                            <Button
                                onClick={props.galleryName === 'Create Video' ? resetVideoCrop : resetCrop}
                                variant="secondary-outline">
                                Reset Crop
                            </Button>
                        }
                        {/* Unlike the always-mounted zoom switches, this one unmounts/remounts
                            (crop mode, reset) — defaultChecked re-reads live state per mount,
                            so the uncontrolled idiom stays in sync */}
                        {cropDateBarAllowedForGallery && croppedImageSrc && mode !== UserMode.CROP &&
                            <Form.Check className="ms-3">
                                <Form.Switch>
                                    <Form.Check.Input
                                        defaultChecked={showCropDateBar}
                                        onClick={() => setShowCropDateBar(!showCropDateBar)}
                                        type="checkbox"
                                        role="switch"
                                        id="showCropDateBar"/>
                                    <Form.Check.Label
                                        htmlFor="showCropDateBar">Date Bar on Cropped Image
                                    </Form.Check.Label>
                                </Form.Switch>
                            </Form.Check>
                        }
                        {props.isSaving && <Spinner size="sm" className="m-2"/>}
                        {/* Opens the current image in a separate, reusable browser tab */}
                        <Button
                            onClick={() => props.onGoToImageInNewTab(props.imageInfo.sequence)}
                            className="ms-3">
                            Go to Image in New Tab
                        </Button>
                        {/* Hidden while a crop is applied: ROIs are never drawn on the cropped
                            view (their coords are full-image-relative), so the switch would be
                            inert there. Remounting re-reads defaultChecked from live state. */}
                        {!croppedImageSrc &&
                            <Form.Check className="ms-3">
                                <Form.Switch>
                                    <Form.Check.Input
                                        defaultChecked={showROI}
                                        onClick={() => setShowROI(!showROI)}
                                        type="checkbox"
                                        role="switch"
                                        id="showRoi"/>
                                    <Form.Check.Label
                                        htmlFor="showRoi">Show Regions
                                    </Form.Check.Label>
                                </Form.Switch>
                            </Form.Check>
                        }
                        {/* Create Video's crop button: same drawing flow as Crop Image, but the
                            box is locked to the fixed 16:9 video ratio and saving stores it as
                            the video image set's ROI (the video's crop framing) before applying
                            the same on-screen crop. Mounted with a crop applied too — clicking
                            starts a redraw, composed against the current crop like the sibling. */}
                        {props.galleryName === 'Create Video' && [UserMode.VIEW, UserMode.ZOOM_PAN, UserMode.CROP].includes(mode) &&
                            <Button
                                data-action="crop"
                                onClick={mode === UserMode.CROP ? saveVideoCrop : enterCropMode}
                                disabled={mode === UserMode.CROP && !pendingCropBox}
                                className="ms-1">
                                {mode === UserMode.CROP ? "Save Video Crop" : "Crop Video to Bounding Box"}
                            </Button>
                        }
                        {/* Same name, same function as the toolbar button - assigns the
                            staged image selection to this day's video image set */}
                        {props.galleryName === 'Create Video' && props.onAssignVideoImages &&
                            <Button
                                onClick={() => props.onAssignVideoImages?.()}
                                className="ms-3">Assign Video Images
                            </Button>
                        }
                    </div>
                </Modal.Footer>
            </Modal>
        </React.Fragment>
    );
}

export default ImagePopupModal;